import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useGetDimensionsList } from '@api/common/queryHooks';
import Table from '@components/common/DimensionTable';
import { useMappedEventLimit } from '@components/common/RulesBuilder/MappedEventBuilder/api';
import { eventDimensionNames } from '@components/dimensions/typed';
import {
  useGetBlockEventsName,
  useGetMappedEvents,
  useGetMappedStatusEvent,
  usePostDimensionData,
} from '@src/api/emp/queryHooks';
import commonSelector from '@store/common/selectors';

import styles from './index.less';

interface IEventExploreProps {
  showDetails?: boolean;
  selectedList?: string[];
  onSelectedChange?: (target: string, selectedList: string[]) => void;
}

export const MAXIMUM_EVENTS_LIMIT = 50;

const EventExplore: React.FC<IEventExploreProps> = (props) => {
  const { selectedList = [], onSelectedChange } = props;
  const [dimensionKey, setDimensionKey] = useState<string>('all_event_name');
  const [dimensionData, setDimensionData] = useState<any[]>([]);
  const { data: allMappedEvents } = useGetMappedEvents({});
  const { data: mappedEventNameData } = useGetMappedStatusEvent('event_name');
  const { data: blockedEventsName } = useGetBlockEventsName();
  const { mutate: getDimensionData, isLoading: isGettingDimensionData } = usePostDimensionData();
  const { data: mappedEventLimitData } = useMappedEventLimit();
  const { data: dimensionList } = useGetDimensionsList();

  const common = useSelector(commonSelector);

  useEffect(() => {
    setDimensionData([]);
    getDimensionData(
      { fields: [dimensionKey], filters: [] },
      {
        onSuccess: (data) => {
          setDimensionData(data?.data?.[0] ?? []);
        },
        onError: (err) => {
          setDimensionData([]);
        },
      },
    );
  }, [dimensionKey, (common as any).interval]);

  const titleOptions = useMemo(
    () =>
      dimensionList?.data
        ?.filter((d) => eventDimensionNames.includes(d.dimension_name))
        ?.map((d) => ({
          label: d.display_name,
          value: d.dimension_name,
        })),
    [dimensionList?.data],
  );

  const onTitleChange = (title: string) => {
    setDimensionKey(title);
  };

  const handleCheckboxClick = (checked: boolean, cellData: string) => {
    let newSelectedList = [...selectedList];
    if (checked) {
      newSelectedList.length < MAXIMUM_EVENTS_LIMIT && newSelectedList.push(cellData);
    } else {
      newSelectedList = selectedList.filter((i) => i !== cellData);
    }
    onSelectedChange?.('', newSelectedList);
  };

  const handleSelectAll = (selectedList: string[]) => {
    onSelectedChange?.('', selectedList);
  };

  return (
    <div className={styles.exploreWrapper}>
      <div className={styles.dimensionTableWrapper}>
        <Table
          lock={false}
          loading={isGettingDimensionData}
          titleDefaultValue={'all_event_name'}
          onTitleChange={onTitleChange}
          titleOptions={titleOptions}
          data={dimensionData}
          mappedStatusList={mappedEventNameData?.data}
          blockedEventsData={blockedEventsName}
          mappedEventDimensionData={allMappedEvents?.data}
          showCheckbox={true}
          selectedList={selectedList}
          onCheckboxClick={handleCheckboxClick}
          rowHighlight={false}
          showSelectAll={true}
          onSelectAll={handleSelectAll}
          selectAllLimit={MAXIMUM_EVENTS_LIMIT}
          selectAllRemainCount={mappedEventLimitData?.data?.remain_count}
          sortByEventStatus
        />
      </div>
    </div>
  );
};

export default EventExplore;
